import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Recuperar preferência salva ou usar preferência do sistema
    const saved = localStorage.getItem('sistemat-theme-mode');
    if (saved === 'light' || saved === 'dark') return saved;
    
    // Verificar preferência do sistema
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Salvar preferência no localStorage
  useEffect(() => {
    localStorage.setItem('sistemat-theme-mode', mode);
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  const setThemeMode = useCallback((next: ThemeMode) => {
    setMode(next);
  }, []);

  /** Campos de formulário (texto, número, select, autocomplete): mesma base visual em todo o sistema */
  const formFieldFontSize = '0.875rem'; // 14px
  const formFieldLabelShrink = '0.75rem'; // 12px — label recolhido (outlined)
  const formFieldHelper = '0.75rem';

  // O tema é caro para recriar; memoizar evita trabalho em renders sem mudança de modo.
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: '#1E4C7D', // Azul Escuro (tipografia)
        light: '#3A6CA8', // Azul Médio
        dark: '#16385E',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#3A6CA8',
        light: '#8DB9E2', // Azul Claro
        dark: '#2C5688',
        contrastText: '#FFFFFF',
      },
      background: {
        default: mode === 'light' ? '#EFF0F2' : '#212121', // Folha do conteúdo; no escuro é mais clara que o chrome
        paper: mode === 'light' ? '#FFFFFF' : '#2A2A2A', // Superfícies sobre a folha
      },
      text: {
        primary: mode === 'light' ? '#000000' : '#F8FAFC',
        secondary: mode === 'light' ? '#4A4A4A' : '#94A3B8', // Cinza Escuro
      },
      divider: mode === 'light' ? '#E2E8F0' : '#334155',
      appHeader: {
        main: mode === 'light' ? '#041121' : '#000000',
        contrastText: '#FFFFFF',
        muted: mode === 'dark' ? '#94A3B8' : 'rgba(255,255,255,0.72)',
        iconBg: 'rgba(255,255,255,0.12)',
        decor: 'rgba(255,255,255,0.35)',
        border: 'rgba(255,255,255,0.12)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Barras de rolagem e controles nativos seguem o tema (sem trilho branco no escuro).
          html: { colorScheme: mode },
          '.recharts-tooltip-wrapper': {
            zIndex: '2 !important',
            pointerEvents: 'none !important',
            outline: 'none !important',
          },
          '.recharts-default-tooltip': {
            backgroundColor: `${mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)'} !important`,
            border: `${mode === 'dark' ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)'} !important`,
            maxWidth: '190px !important',
            maxHeight: '150px !important',
            overflow: 'hidden !important',
            padding: '6px 8px !important',
            borderRadius: '8px !important',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15) !important',
            fontSize: '10px !important',
            lineHeight: '1.15 !important',
          },
          '.recharts-tooltip-label': {
            marginBottom: '2px !important',
            fontSize: '10px !important',
            fontWeight: '700 !important',
            lineHeight: '1.15 !important',
            overflow: 'hidden !important',
            textOverflow: 'ellipsis !important',
            whiteSpace: 'nowrap !important',
          },
          '.recharts-tooltip-item-list': {
            margin: '0 !important',
            padding: '0 !important',
          },
          '.recharts-tooltip-item': {
            display: 'flex !important',
            justifyContent: 'space-between !important',
            gap: '8px !important',
            padding: '0 !important',
            lineHeight: '1.15 !important',
            whiteSpace: 'nowrap !important',
          },
          '.recharts-tooltip-item-name': {
            minWidth: '0 !important',
            overflow: 'hidden !important',
            textOverflow: 'ellipsis !important',
          },
          '.recharts-tooltip-item-separator': {
            display: 'none !important',
          },
          '.recharts-tooltip-item-value': {
            fontWeight: '700 !important',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            ...(mode === 'dark' && {
              // Mesmo tom do chrome (sidebar); sem !important para o sx de cada AppBar valer.
              backgroundColor: '#171717',
              // Remove overlay/gradient do MUI no dark (Paper overlay)
              backgroundImage: 'none !important',
            }),
            backgroundImage: 'none',
            boxShadow: mode === 'light'
              ? '0 4px 20px rgba(30, 41, 59, 0.25)'
              : '0 4px 20px rgba(0, 0, 0, 0.5)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // Removido override global - estilos aplicados apenas na sidebar específica
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: '#F8FAFC', // Texto claro para contraste no fundo escuro
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#2A2A2A' : '#334155',
              color: mode === 'dark' ? '#FFFFFF' : '#8DB9E2',
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? '#2A2A2A' : '#3A6CA8',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: mode === 'dark' ? '#333333' : '#2C5688',
              },
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'light'
                ? 'rgba(141, 185, 226, 0.15)'
                : 'rgba(255, 255, 255, 0.1)',
            },
            // Ícones de edição (lápis) ficam brancos no modo dark quando color="primary"
            ...(mode === 'dark' && {
              '&.MuiIconButton-colorPrimary': {
                color: '#FFFFFF !important',
                '& .MuiSvgIcon-root': {
                  color: '#FFFFFF !important',
                },
                '&:hover': {
                  color: '#FFFFFF !important',
                  '& .MuiSvgIcon-root': {
                    color: '#FFFFFF !important',
                  },
                },
              },
            }),
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? 'text.primary' : 'text.primary',
            '&.Mui-selected': {
              color: mode === 'light' ? 'primary.main' : '#FFFFFF',
            },
            '&:hover': {
              color: mode === 'light' ? 'primary.main' : '#FFFFFF',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          textPrimary: {
            ...(mode === 'dark' && {
              color: '#F8FAFC',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }),
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: mode === 'light' ? 'primary.main' : '#FFFFFF',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            // Sem moldura: só as linhas entre linhas da tabela; corpo usa fundo da página
            '&.MuiTableContainer-root': {
              backgroundColor: 'transparent',
              backgroundImage: 'none',
              boxShadow: 'none',
              border: 'none',
            },
            // Não aplicar em Dialog: o Paper do modal também contém TableContainer e ficaria transparente
            '&:has(.MuiTableContainer-root):not(.MuiDialog-paper)': {
              backgroundColor: 'transparent',
              backgroundImage: 'none',
              boxShadow: 'none',
              border: 'none',
            },
          },
        },
      },
      MuiTable: {
        defaultProps: {
          // Cabeçalho fixo ao rolar o TableContainer (já com overflow/maxHeight no tema).
          // Desative por tabela com stickyHeader={false} se necessário.
          stickyHeader: true,
        },
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            '& .MuiTableCell-head': {
              color: mode === 'light' ? '#1E4C7D' : '#FFFFFF',
              fontWeight: 600,
              borderBottom: `3px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.16)'}`,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            height: 38,
            minHeight: 38,
            '&:not(.MuiTableRow-head)': {
              backgroundColor: 'transparent',
            },
            // Só linhas com hover={true} (corpo da tabela); cabeçalho/rodapé não têm .MuiTableRow-hover
            '&.MuiTableRow-hover:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
            },
            '&.MuiTableRow-head': {
              height: 38,
              minHeight: 38,
              backgroundColor: 'transparent',
            },
            // Última linha do corpo: sem traço embaixo (só divisórias entre linhas)
            '.MuiTableBody-root &:last-of-type .MuiTableCell-body': {
              borderBottom: 'none',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.mode === 'light' ? '#000000' : undefined,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '4px 12px',
            lineHeight: 1.15,
            '&.MuiTableCell-head': {
              padding: '6px 12px',
              textTransform: 'uppercase',
              // Obrigatório com stickyHeader: senão o corpo aparece “por trás” ao rolar.
              backgroundColor: theme.palette.background.paper,
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              ...(theme.palette.mode === 'dark' && { color: '#FFFFFF' }),
            },
            // Corpo: só divisor horizontal entre linhas (sem moldura nem bordas verticais)
            '&.MuiTableCell-body, &.MuiTableCell-footer': {
              backgroundColor: 'transparent',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
            },
          }),
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            border: 'none',
            // Garante scroll vertical quando a tabela ficar grande,
            // mantendo visual compacto e adaptando à altura da viewport.
            overflowY: 'auto',
            maxHeight: 'clamp(320px, calc(100dvh - 280px), 760px)',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: formFieldFontSize,
          },
          input: {
            fontSize: formFieldFontSize,
            color: mode === 'light' ? '#000000' : undefined,
          },
          inputMultiline: {
            fontSize: formFieldFontSize,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontSize: formFieldFontSize,
            '&.MuiInputBase-sizeSmall': {
              minHeight: 42,
            },
            '& fieldset': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : undefined,
            },
            '&:hover fieldset': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : undefined,
            },
            '&.Mui-focused fieldset': {
              borderColor: mode === 'dark' ? '#FFFFFF !important' : undefined, // Branco quando focado
              borderWidth: mode === 'dark' ? '2px' : undefined,
            },
            '&.Mui-focused .MuiInputBase-input': {
              color: mode === 'dark' ? '#FFFFFF !important' : undefined, // Texto branco quando focado
            },
            '&.Mui-error fieldset': {
              borderColor: mode === 'dark' ? '#d32f2f' : undefined,
            },
          },
          input: {
            fontSize: formFieldFontSize,
          },
          inputSizeSmall: {
            fontSize: formFieldFontSize,
            padding: '6px 12px',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: formFieldFontSize,
            '&.Mui-focused': {
              color: mode === 'dark' ? '#FFFFFF' : undefined, // Branco quando focado
            },
          },
          outlined: {
            '&.MuiInputLabel-shrink': {
              fontSize: formFieldLabelShrink,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: formFieldHelper,
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: formFieldFontSize,
            minHeight: 34,
            paddingTop: 5,
            paddingBottom: 5,
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          paper: {
            fontSize: formFieldFontSize,
          },
          option: {
            fontSize: formFieldFontSize,
          },
          input: {
            fontSize: formFieldFontSize,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: formFieldFontSize,
          },
        },
      },
    },
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
  }), [mode]);

  const value = useMemo<ThemeContextType>(() => ({
    mode,
    toggleTheme,
    setThemeMode,
    isDarkMode: mode === 'dark',
  }), [mode, toggleTheme, setThemeMode]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
